import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { notifications } from '@mantine/notifications';
import { useSearchParams } from 'react-router-dom';
import {
  CurrentSessionContext,
  type CurrentSessionContextValue,
} from '@/hooks/useCurrentSession';
import { useSessionManagement } from '@/hooks/useSessionManagement';

const SESSION_ID_PARAM = 'session_id';
const SESSION_STORAGE_KEY = 'current_session_id';

const parseSessionId = (value: string | null): number | null => {
  if (!value || !/^[1-9]\d*$/.test(value)) {
    return null;
  }

  const sessionId = Number(value);
  return Number.isSafeInteger(sessionId) ? sessionId : null;
};

interface CurrentSessionProviderProps {
  children: ReactNode;
}

export const CurrentSessionProvider = ({ children }: CurrentSessionProviderProps) => {
  const { sessions, isLoading } = useSessionManagement();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastWarnedSessionId = useRef<string | null>(null);

  const requestedSessionIdValue = searchParams.get(SESSION_ID_PARAM);
  const requestedSessionId = parseSessionId(requestedSessionIdValue);

  const currentSessionId = useMemo(() => {
    if (isLoading || sessions.length === 0) {
      return null;
    }

    if (
      requestedSessionId !== null &&
      sessions.some((session) => session.intake_portal_session_id === requestedSessionId)
    ) {
      return requestedSessionId;
    }

    const storedSessionId = parseSessionId(localStorage.getItem(SESSION_STORAGE_KEY));
    if (
      storedSessionId !== null &&
      sessions.some((session) => session.intake_portal_session_id === storedSessionId)
    ) {
      return storedSessionId;
    }

    return sessions[0].intake_portal_session_id;
  }, [isLoading, requestedSessionId, sessions]);

  useEffect(() => {
    if (currentSessionId === null) {
      return;
    }

    localStorage.setItem(SESSION_STORAGE_KEY, currentSessionId.toString());

    const canonicalSessionId = currentSessionId.toString();
    if (requestedSessionIdValue === canonicalSessionId) {
      lastWarnedSessionId.current = null;
      return;
    }

    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set(SESSION_ID_PARAM, canonicalSessionId);
      return nextParams;
    }, { replace: true });

    if (
      requestedSessionIdValue !== null &&
      lastWarnedSessionId.current !== requestedSessionIdValue
    ) {
      lastWarnedSessionId.current = requestedSessionIdValue;
      notifications.show({
        color: 'yellow',
        title: 'Session unavailable',
        message: `Session ${requestedSessionIdValue} is not available. Switched to session ${canonicalSessionId}.`,
      });
    }
  }, [currentSessionId, requestedSessionIdValue, setSearchParams]);

  const selectSession = useCallback((sessionId: number) => {
    if (!Number.isSafeInteger(sessionId) || sessionId <= 0) {
      return;
    }

    localStorage.setItem(SESSION_STORAGE_KEY, sessionId.toString());
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set(SESSION_ID_PARAM, sessionId.toString());
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const currentSession = sessions.find(
    (session) => session.intake_portal_session_id === currentSessionId,
  );

  const value = useMemo<CurrentSessionContextValue>(() => ({
    currentSessionId,
    currentSession,
    sessions,
    isLoading,
    selectSession,
  }), [currentSession, currentSessionId, isLoading, selectSession, sessions]);

  return (
    <CurrentSessionContext.Provider value={value}>
      <Fragment key={currentSessionId ?? 'no-session'}>
        {children}
      </Fragment>
    </CurrentSessionContext.Provider>
  );
};
